import { Competency_Map_Status, CompetencyMapQuery } from "@generated/graphql";
import { BoltOutlined, DashboardCustomizeOutlined, DeleteOutline, PlayArrowOutlined } from "@mui/icons-material";
import { generateUnavailableText } from "components/buttons/ButtonWithPermissions";
import { COMPETENCY_MAP_STATUS_TITLE } from "constants/global";
import { CompanyContext } from "context/companyContext";
import { ConfirmModalContext } from "context/confirmModalContext";
import { CompetencyMapSelectedParts } from "helpers/competencyMapSelectParts";
import useCreateTesting from "hooks/useCreateTesting";
import { Dispatch, SetStateAction, useContext } from "react";
import { UseFieldArrayReplace, UseFormReturn } from "react-hook-form";
import useCompetencyMapController from "../Header/components/useController";
import { CompetencyMapInputs } from "../types";
import SelectionActionBar from "./SelectionActionBar";
import { SelectedEntityIds, SelectionScope, SelectionTree } from "./types";
import {
  getSelectedEntityCountsLabel,
  getSelectedEntityDeleteDescription,
  getSelectedParts,
  hasSelectedEntities,
  hasSelectedQuestions,
  removeSelectedEntitiesFromKnowledgeBlocks,
} from "./utils";

export interface SelectionActionsProps {
  competencyMap: CompetencyMapQuery["competencyMap"];
  isCreate: boolean;
  setIsEditable: Dispatch<SetStateAction<boolean>>;
  setSelectedEntities: Dispatch<SetStateAction<SelectedEntityIds>>;
  form: UseFormReturn<CompetencyMapInputs>;
  replaceKnowledgeBlocks: UseFieldArrayReplace<CompetencyMapInputs, "knowledgeBlocks">;
  selectedEntities: SelectedEntityIds;
  selectionTree: SelectionTree;
  selectionScope: SelectionScope | null;
  onClearSelectionMode: () => void;
  onOpenGenerateQuestions: (selectParts: CompetencyMapSelectedParts) => void;
  onOpenCopyMap: (selectParts: CompetencyMapSelectedParts) => void;
}

const SelectionActions = ({
  competencyMap,
  isCreate,
  setIsEditable,
  setSelectedEntities,
  form,
  replaceKnowledgeBlocks,
  selectedEntities,
  selectionTree,
  selectionScope,
  onClearSelectionMode,
  onOpenGenerateQuestions,
  onOpenCopyMap,
}: SelectionActionsProps) => {
  const { handleOpen } = useContext(ConfirmModalContext);
  const { isCreateAvailable, currentCompany } = useContext(CompanyContext);
  const { createTesting } = useCreateTesting({ competencyMap });
  const { onSubmitWithOptions, loading, knowledgeBlockLoading, skillLoading } = useCompetencyMapController({
    competencyMap,
    isCreate,
    setIsEditable,
  });

  const selectionHasQuestions = hasSelectedQuestions(selectedEntities, selectionTree);
  const hasSelected = hasSelectedEntities(selectedEntities);
  const isDeleteLoading = loading || knowledgeBlockLoading || skillLoading;
  const isOpen = selectionScope === "constructor";
  const emptySelectionTooltip = "Необходимо выбрать хотя бы один элемент";
  const isTestingStatusDisabled = competencyMap?.status !== Competency_Map_Status.Approved;
  const createTestingUnavailableText = generateUnavailableText(
    isCreateAvailable,
    currentCompany,
    "создания тестирования"
  );
  const createTestingStatusTooltip = isTestingStatusDisabled
    ? `Создание тестирования доступно только в статусе «${COMPETENCY_MAP_STATUS_TITLE.APPROVED}»`
    : undefined;
  const createTestingTooltip =
    (!hasSelected && emptySelectionTooltip) ||
    (!selectionHasQuestions && "В выборке нет вопросов") ||
    createTestingUnavailableText ||
    createTestingStatusTooltip ||
    "Запустить тест только с вопросами из выбранных объектов";
  const isCreateTestingDisabled =
    !hasSelected || !selectionHasQuestions || !isCreateAvailable || isTestingStatusDisabled;

  const handleGenerateQuestions = () => {
    const selectParts = getSelectedParts(selectedEntities);
    if (!selectParts) return;

    onOpenGenerateQuestions(selectParts);
  };

  const handleCreateTesting = () => {
    const selectParts = getSelectedParts(selectedEntities);
    if (!selectParts) return;

    createTesting(selectParts);
  };

  const handleCreateMap = () => {
    const selectParts = getSelectedParts(selectedEntities);
    if (!selectParts) return;

    onOpenCopyMap(selectParts);
  };

  const handleDelete = () => {
    handleOpen({
      title: "Удалить выбранные элементы?",
      description: getSelectedEntityDeleteDescription(selectedEntities),
      actionButtonText: "Удалить",
      handleClick: async () => {
        const previousKnowledgeBlocks = form.getValues("knowledgeBlocks");
        const previousSelectedEntities = selectedEntities;
        const { knowledgeBlocks, deletedSkillIds } = removeSelectedEntitiesFromKnowledgeBlocks(
          previousKnowledgeBlocks,
          selectedEntities
        );

        replaceKnowledgeBlocks(knowledgeBlocks);

        const isSuccess = await onSubmitWithOptions(
          {
            ...form.getValues(),
            knowledgeBlocks,
          },
          { skillsIdsToDelete: deletedSkillIds }
        );

        if (isSuccess) {
          onClearSelectionMode();
          return;
        }

        replaceKnowledgeBlocks(previousKnowledgeBlocks || []);
        setSelectedEntities(previousSelectedEntities);
      },
    });
  };

  return (
    <SelectionActionBar
      open={isOpen}
      selectionLabel={getSelectedEntityCountsLabel(selectedEntities)}
      actions={[
        {
          id: "generate",
          title: "Сгенерировать вопросы",
          tooltip: hasSelected ? "Сгенерировать вопросы только для выбранных объектов" : emptySelectionTooltip,
          onClick: handleGenerateQuestions,
          disabled: !hasSelected,
          color: "third",
          startIcon: <BoltOutlined />,
        },
        {
          id: "testing",
          title: "Создать тестирование",
          tooltip: createTestingTooltip,
          onClick: handleCreateTesting,
          disabled: isCreateTestingDisabled,
          startIcon: <PlayArrowOutlined />,
        },
        {
          id: "create-map",
          title: "Создать карту",
          tooltip: hasSelected ? "Новая карта компетенций из выбранных объектов" : emptySelectionTooltip,
          onClick: handleCreateMap,
          disabled: !hasSelected,
          color: "third",
          startIcon: <DashboardCustomizeOutlined />,
        },
        {
          id: "delete",
          title: "Удалить",
          tooltip: hasSelected ? "Удалить выбранные объекты и все их вложения из карты" : emptySelectionTooltip,
          onClick: handleDelete,
          disabled: !hasSelected,
          loading: isDeleteLoading,
          color: "error",
          startIcon: <DeleteOutline />,
        },
      ]}
      onClose={onClearSelectionMode}
    />
  );
};

export default SelectionActions;
